export interface KroKrwIntersectionResult {
  points: Array<{ sw: number; kro: number; krw: number }>;
  intersectionSw: number | null;
  intersectionK: number | null;
  status: 'valid' | 'invalid-input' | 'no-intersection';
  invalidRange: boolean;
}

interface KroKrwInputs {
  swValues: Array<number | null | undefined>;
  swi: number | null | undefined;
  sor: number | null | undefined;
  no: number | null | undefined;
  nw: number | null | undefined;
  krwEndPoint: number | null | undefined;
}

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const calculateKroKrwIntersection = (
  inputs: KroKrwInputs
): KroKrwIntersectionResult => {
  const { swValues, swi, sor, no, nw, krwEndPoint } = inputs;

  if (
    !isFiniteNumber(swi) ||
    !isFiniteNumber(sor) ||
    !isFiniteNumber(no) ||
    !isFiniteNumber(nw) ||
    !isFiniteNumber(krwEndPoint)
  ) {
    return {
      points: [],
      intersectionSw: null,
      intersectionK: null,
      status: 'invalid-input',
      invalidRange: false
    };
  }

  const denom = 1 - swi - sor;
  if (denom <= 0) {
    return {
      points: [],
      intersectionSw: null,
      intersectionK: null,
      status: 'invalid-input',
      invalidRange: false
    };
  }

  const points: Array<{ sw: number; kro: number; krw: number }> = [];
  let invalidRange = false;

  (swValues ?? []).forEach(swValue => {
    if (!isFiniteNumber(swValue)) {
      return;
    }

    if (swValue < swi || swValue > 1 - sor) {
      invalidRange = true;
      return;
    }

    const swn = (swValue - swi) / denom;
    const son = (1 - swValue - sor) / denom;

    if (swn < 0 || son < 0) {
      return;
    }

    const kro = Math.pow(son, no);
    const krw = krwEndPoint * Math.pow(swn, nw);

    if (!Number.isFinite(kro) || !Number.isFinite(krw)) {
      return;
    }

    points.push({ sw: swValue, kro, krw });
  });

  if (points.length === 0) {
    return {
      points,
      intersectionSw: null,
      intersectionK: null,
      status: 'no-intersection',
      invalidRange
    };
  }

  for (let i = 0; i < points.length - 1; i += 1) {
    const current = points[i];
    const next = points[i + 1];
    const diffCurrent = current.kro - current.krw;
    const diffNext = next.kro - next.krw;

    if (diffCurrent === 0) {
      return {
        points,
        intersectionSw: current.sw,
        intersectionK: current.kro,
        status: 'valid',
        invalidRange
      };
    }

    if (diffCurrent * diffNext < 0 || diffNext === 0) {
      const divisor = diffCurrent - diffNext;
      const t = divisor === 0 ? 0 : diffCurrent / divisor;
      const intersectionSw = current.sw + t * (next.sw - current.sw);
      const intersectionK = current.kro + t * (next.kro - current.kro);

      return {
        points,
        intersectionSw,
        intersectionK,
        status: 'valid',
        invalidRange
      };
    }
  }

  return {
    points,
    intersectionSw: null,
    intersectionK: null,
    status: 'no-intersection',
    invalidRange
  };
};
